// State hook for the Order Intake review panel (OI-3).
//
// Composes the shared review hook with the manufacturer-only create flow.

import { useCallback, useState } from "react";

import { createOrderIntake, importIdeaRoomOrderIntake, importSenseiOrderIntake, type CreateIntakeBody } from "@/lib/orderIntakeClient";

import { useOrderIntakeReview } from "@/components/sales-intake/useOrderIntakeReview";

export type { IntakeTab } from "@/components/sales-intake/useOrderIntakeReview";
export { INTAKE_TABS } from "@/components/sales-intake/useOrderIntakeReview";

export type UseManufacturingOrderIntakeState = ReturnType<typeof useManufacturingOrderIntake>;

export function useManufacturingOrderIntake() {
  const review = useOrderIntakeReview();

  const [createOpen, setCreateOpenState] = useState(false);
  const [importOpen, setImportOpenState] = useState(false);
  const [senseiOpen, setSenseiOpenState] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const setCreateOpen = useCallback((open: boolean) => {
    setCreateOpenState(open);
    if (open) {
      setImportOpenState(false);
      setSenseiOpenState(false);
    }
    if (!open) setCreateError(null);
  }, []);

  const setImportOpen = useCallback((open: boolean) => {
    setImportOpenState(open);
    if (open) {
      setCreateOpenState(false);
      setSenseiOpenState(false);
    }
    if (!open) setCreateError(null);
  }, []);

  const setSenseiOpen = useCallback((open: boolean) => {
    setSenseiOpenState(open);
    if (open) {
      setCreateOpenState(false);
      setImportOpenState(false);
    }
    if (!open) setCreateError(null);
  }, []);

  const submitCreate = useCallback(
    async (body: CreateIntakeBody): Promise<boolean> => {
      setCreateBusy(true);
      setCreateError(null);
      const r = await createOrderIntake(body);
      setCreateBusy(false);
      if (!r.ok) {
        setCreateError(r.error);
        return false;
      }
      setCreateOpenState(false);
      await review.goToTab("pending_review");
      return true;
    },
    [review],
  );

  const submitIdeaRoomImport = useCallback(
    async (payload: unknown): Promise<boolean> => {
      setCreateBusy(true);
      setCreateError(null);
      const r = await importIdeaRoomOrderIntake(payload);
      setCreateBusy(false);
      if (!r.ok) {
        setCreateError(r.error);
        return false;
      }
      setImportOpenState(false);
      await review.goToTab("pending_review");
      return true;
    },
    [review],
  );

  const submitSenseiImport = useCallback(
    async (payload: unknown): Promise<boolean> => {
      setCreateBusy(true);
      setCreateError(null);
      const r = await importSenseiOrderIntake(payload);
      setCreateBusy(false);
      if (!r.ok) {
        setCreateError(r.error);
        return false;
      }
      setSenseiOpenState(false);
      await review.goToTab("pending_review");
      return true;
    },
    [review],
  );

  return {
    ...review,
    createOpen,
    setCreateOpen,
    importOpen,
    setImportOpen,
    senseiOpen,
    setSenseiOpen,
    createBusy,
    createError,
    submitCreate,
    submitIdeaRoomImport,
    submitSenseiImport,
  };
}
